import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PickupPointsManager = ({ pickupPoints, favoritePoints, operatingHours }) => {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showMap, setShowMap] = useState(false);

  const formatTime = (time) => {
    return new Intl.DateTimeFormat('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })?.format(new Date(`2000-01-01T${time}`));
  };

  const getWalkingTime = (distance) => {
    // Assuming average walking speed of 5 km/h
    const walkingMinutes = Math.round((distance / 5) * 60);
    return walkingMinutes;
  };

  const isCurrentlyOpen = (hours) => {
    if (!hours || !hours?.open || !hours?.close) {
      return false;
    }
    
    const now = new Date();
    const currentTime = now?.getHours() * 60 + now?.getMinutes();
    
    // Safely parse open time
    const openTimeParts = hours?.open?.split(':');
    if (openTimeParts?.length !== 2) return false;
    const openHour = parseInt(openTimeParts?.[0], 10) || 0;
    const openMin = parseInt(openTimeParts?.[1], 10) || 0;
    
    // Safely parse close time
    const closeTimeParts = hours?.close?.split(':');
    if (closeTimeParts?.length !== 2) return false;
    const closeHour = parseInt(closeTimeParts?.[0], 10) || 0;
    const closeMin = parseInt(closeTimeParts?.[1], 10) || 0;
    
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    
    return currentTime >= openTime && currentTime <= closeTime;
  };

  const getStatusColor = (point) => {
    if (!isCurrentlyOpen(point?.hours)) return 'var(--color-error)';
    if (point?.availability === 'high') return 'var(--color-success)';
    if (point?.availability === 'medium') return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const getStatusText = (point) => {
    if (!isCurrentlyOpen(point?.hours)) return 'Cerrado';
    if (point?.availability === 'high') return 'Disponible';
    if (point?.availability === 'medium') return 'Ocupado';
    return 'Lleno';
  };

  const toggleFavorite = (pointId) => {
    // Handle favorite toggle logic
    console.log('Toggle favorite for point:', pointId);
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="MapPin" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground">
              Puntos de Recogida Campus
            </h3>
            <p className="text-sm text-muted-foreground">
              Ubicaciones disponibles en el campus
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowMap(!showMap)}
        >
          <Icon name={showMap ? 'List' : 'Map'} size={16} className="mr-2" />
          {showMap ? 'Lista' : 'Mapa'}
        </Button>
      </div>
      {/* Favorite Points Quick Access */}
      {favoritePoints?.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Heart" size={16} color="var(--color-accent)" />
            <span>Puntos Favoritos</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {favoritePoints?.map((point) => (
              <div
                key={point?.id}
                className="flex items-center justify-between p-3 bg-accent/5 border border-accent/20 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center">
                    <Icon name={point?.icon} size={16} color="var(--color-accent)" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground text-sm">{point?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {getWalkingTime(point?.distance)} min caminando
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full`} style={{ backgroundColor: getStatusColor(point) }} />
                  <span className="text-xs text-muted-foreground">
                    {getStatusText(point)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Map View */}
      {showMap && (
        <div className="mb-6">
          <div className="aspect-video bg-muted rounded-lg overflow-hidden">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title="Campus Pickup Points Map"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=40.4168,-3.7038&z=16&output=embed"
              className="border-0"
            />
          </div>
        </div>
      )}
      {/* Pickup Points List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-foreground">Todos los Puntos</h4>
          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-success" />
              <span>Disponible</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-warning" />
              <span>Ocupado</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-error" />
              <span>Cerrado</span>
            </div>
          </div>
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {pickupPoints?.map((point) => (
            <div
              key={point?.id}
              className={`border rounded-lg p-4 transition-smooth cursor-pointer ${
                selectedPoint?.id === point?.id
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
              onClick={() => setSelectedPoint(selectedPoint?.id === point?.id ? null : point)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                    <Icon name={point?.icon} size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h5 className="font-medium text-foreground">{point?.name}</h5>
                    <p className="text-sm text-muted-foreground">{point?.location}</p>
                    <div className="flex items-center space-x-4 mt-1">
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
                        <span className="text-xs text-muted-foreground">
                          {formatTime(point?.hours?.open)} - {formatTime(point?.hours?.close)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="MapPin" size={12} color="var(--color-muted-foreground)" />
                        <span className="text-xs text-muted-foreground">
                          {getWalkingTime(point?.distance)} min
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: getStatusColor(point) }}
                      />
                      <span className="text-sm font-medium text-foreground">
                        {getStatusText(point)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {point?.currentCapacity}/{point?.maxCapacity} ocupado
                    </p>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e?.stopPropagation();
                      toggleFavorite(point?.id);
                    }}
                  >
                    <Icon 
                      name={favoritePoints?.some(fp => fp?.id === point?.id) ? 'Heart' : 'HeartOff'} 
                      size={16} 
                      color={favoritePoints?.some(fp => fp?.id === point?.id) ? 'var(--color-accent)' : 'var(--color-muted-foreground)'}
                    />
                  </Button>
                </div>
              </div>

              {/* Expanded Details */}
              {selectedPoint?.id === point?.id && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h6 className="font-medium text-foreground mb-2">Servicios Disponibles</h6>
                      <div className="space-y-1">
                        {point?.services?.map((service, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <Icon name="Check" size={14} color="var(--color-success)" />
                            <span className="text-sm text-muted-foreground">{service}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h6 className="font-medium text-foreground mb-2">Información Adicional</h6>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Icon name="Users" size={14} color="var(--color-muted-foreground)" />
                          <span className="text-sm text-muted-foreground">
                            Capacidad máxima: {point?.maxCapacity} personas
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Wifi" size={14} color="var(--color-muted-foreground)" />
                          <span className="text-sm text-muted-foreground">
                            WiFi disponible
                          </span>
                        </div>
                        {point?.accessibility && (
                          <div className="flex items-center space-x-2">
                            <Icon name="Accessibility" size={14} color="var(--color-success)" />
                            <span className="text-sm text-muted-foreground">
                              Accesible para personas con discapacidad
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 mt-4">
                    <Button variant="default" size="sm">
                      <Icon name="Navigation" size={14} className="mr-2" />
                      Cómo Llegar
                    </Button>
                    <Button variant="outline" size="sm">
                      <Icon name="Phone" size={14} className="mr-2" />
                      Contactar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PickupPointsManager;